(function () {
  'use strict';

  const doc = window.document;

  function getLuminance(r, g, b) {
    const [rs, gs, bs] = [r, g, b].map((c) => {
      c = c / 255;
      return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    });
    return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
  }

  function getContrastRatio(l1, l2) {
    const lighter = Math.max(l1, l2);
    const darker = Math.min(l1, l2);
    return (lighter + 0.05) / (darker + 0.05);
  }

  function parseColor(color) {
    if (!color) return null;
    const temp = doc.createElement('div');
    temp.style.color = color;
    doc.body.appendChild(temp);
    const computed = getComputedStyle(temp).color;
    doc.body.removeChild(temp);

    const m = computed.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
    if (!m) return null;
    return { r: +m[1], g: +m[2], b: +m[3] };
  }

  function getCssVarColor(varName, fallback) {
    const v = getComputedStyle(doc.documentElement).getPropertyValue(varName).trim();
    return v || fallback;
  }

  function setCheckmarkTheme(checkmark, usePuce) {
    const puce = getCssVarColor('--french-puce', '#451609');
    const white = getCssVarColor('--simple-white', '#ffffff');
    const c = usePuce ? puce : white;
    checkmark.style.color = c;
    checkmark.style.fill = c;
  }

  function colorFromGradientString(bg) {
    if (!bg || !bg.includes('gradient')) return null;
    const matches = bg.match(/#[0-9a-f]{3,8}|rgba?\([^)]+\)|hsla?\([^)]+\)/gi);
    if (!matches || !matches.length) return null;

    const parsed = matches.map(parseColor).filter(Boolean);
    if (!parsed.length) return null;

    const avg = parsed.reduce(
      (a, c) => ({ r: a.r + c.r, g: a.g + c.g, b: a.b + c.b }),
      { r: 0, g: 0, b: 0 }
    );

    return {
      r: Math.round(avg.r / parsed.length),
      g: Math.round(avg.g / parsed.length),
      b: Math.round(avg.b / parsed.length),
    };
  }

  function parseBackgroundImageUrl(bgImage) {
    if (!bgImage || bgImage === 'none') return null;
    const m = bgImage.match(/url\((['"]?)(.*?)\1\)/i);
    return m ? m[2] : null;
  }

  function sampleImageAverageColor(url, sampleSize = 24) {
    return new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.referrerPolicy = 'no-referrer';

      img.onload = () => {
        try {
          const canvas = doc.createElement('canvas');
          canvas.width = sampleSize;
          canvas.height = sampleSize;
          const ctx = canvas.getContext('2d', { willReadFrequently: true });
          if (!ctx) return resolve(null);

          ctx.drawImage(img, 0, 0, sampleSize, sampleSize);
          const data = ctx.getImageData(0, 0, sampleSize, sampleSize).data;

          let r = 0, g = 0, b = 0, n = 0;
          for (let i = 0; i < data.length; i += 4) {
            const a = data[i + 3];
            if (a < 16) continue;
            r += data[i];
            g += data[i + 1];
            b += data[i + 2];
            n++;
          }
          if (!n) return resolve(null);

          resolve({
            r: Math.round(r / n),
            g: Math.round(g / n),
            b: Math.round(b / n),
          });
        } catch (_) {
          resolve(null);
        }
      };

      img.onerror = () => resolve(null);
      img.src = url;
    });
  }

  function chooseCheckmarkForBg(bgColor) {
    if (!bgColor) return false;

    const bgLum = getLuminance(bgColor.r, bgColor.g, bgColor.b);

    const whiteLum = 1;
    const puceRgb =
      parseColor(getCssVarColor('--french-puce', '#451609')) || { r: 69, g: 22, b: 9 };
    const puceLum = getLuminance(puceRgb.r, puceRgb.g, puceRgb.b);

    const whiteContrast = getContrastRatio(bgLum, whiteLum);
    const puceContrast = getContrastRatio(bgLum, puceLum);

    return puceContrast > whiteContrast;
  }

  async function setCheckmarkColor(swatchElement) {
    const checkmark = swatchElement.querySelector('.checkmark');
    if (!checkmark) return;

    const inlineBg = swatchElement.style.background || '';
    const inlineBgColor = swatchElement.style.backgroundColor || '';
    const inlineBgImage = swatchElement.style.backgroundImage || '';

    const cs = getComputedStyle(swatchElement);
    const computedBgColor = cs.backgroundColor;
    const computedBgImage = cs.backgroundImage;

    const grad = colorFromGradientString(inlineBg) || colorFromGradientString(computedBgImage);
    if (grad) {
      setCheckmarkTheme(checkmark, chooseCheckmarkForBg(grad));
      return;
    }

    const solid = parseColor(inlineBgColor) || parseColor(computedBgColor);
    const hasImage =
      (inlineBgImage && inlineBgImage !== 'none') || (computedBgImage && computedBgImage !== 'none');

    if (
      solid &&
      !(solid.r === 0 && solid.g === 0 && solid.b === 0 && String(computedBgColor).includes('0)') && hasImage)
    ) {
      setCheckmarkTheme(checkmark, chooseCheckmarkForBg(solid));
      return;
    }

    const imgUrl = parseBackgroundImageUrl(inlineBgImage) || parseBackgroundImageUrl(computedBgImage);
    if (imgUrl) {
      const avg = await sampleImageAverageColor(imgUrl);
      if (avg) {
        setCheckmarkTheme(checkmark, chooseCheckmarkForBg(avg));
        return;
      }
    }

    setCheckmarkTheme(checkmark, false);
  }

  async function updateScope(selector) {
    const nodes = Array.from(doc.querySelectorAll(selector));
    await Promise.all(nodes.map(setCheckmarkColor));
  }

  async function initializeSwatchContrast() {
    await Promise.all([
      updateScope('.item-link .product-colour'),
      updateScope('#modal-product-variations .product-colour'),
      updateScope('.product-header_option-list .product-colour'),

      // collection / filter swatches
      updateScope('.filter-swatch.is-active .filter-swatch__color'),
    ]);
  }

  function observeSwatchChanges() {
    const root = doc.body;
    if (!root) return;

    const mo = new MutationObserver((mutations) => {
      let shouldRun = false;

      for (const m of mutations) {
        if (
          m.type === 'childList' ||
          (m.type === 'attributes' &&
            (m.attributeName === 'style' || m.attributeName === 'class' || m.attributeName === 'x-show'))
        ) {
          shouldRun = true;
          break;
        }
      }

      if (shouldRun) {
        clearTimeout(observeSwatchChanges._t);
        observeSwatchChanges._t = setTimeout(() => initializeSwatchContrast(), 40);
      }
    });

    mo.observe(root, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class', 'x-show'],
    });
  }

  function boot() {
    initializeSwatchContrast();
    observeSwatchChanges();
  }

  if (doc.readyState === 'loading') {
    window.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  window.addEventListener('variant:changed', initializeSwatchContrast);
  doc.addEventListener('variant:change', initializeSwatchContrast);
  window.addEventListener('aw-variant-changed', initializeSwatchContrast);
  window.addEventListener('aw-card-variant-changed', initializeSwatchContrast);
  doc.addEventListener('wc_fragments_refreshed', initializeSwatchContrast);
  doc.addEventListener('shopify:section:load', initializeSwatchContrast);

  window.updateSwatchContrast = initializeSwatchContrast;
})();